import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPaperBookCaveat } from '../services/api';

// Texte canonique de repli — TROISIÈME copie du même avertissement (cf.
// sniper_elite/workers/paper_slippage.py::AVERTISSEMENT_LIVRE_PAPIER et
// dashboard/backend/src/main.py::_AVERTISSEMENT_LIVRE_PAPIER). Verrouillée par
// sniper_elite/tests/test_avertissement_livre_papier.py (comparaison des
// trois copies) — une divergence entre les trois est un bug.
const AVERTISSEMENT_LIVRE_PAPIER_REPLI = (
  "AVERTISSEMENT : le livre papier est OPTIMISTE — le modèle de slippage " +
  "sous-déduit la friction réelle (biais mesuré -14,68 pp, agrégat sur " +
  "l'échantillon live de référence, pas une correction ligne à ligne — " +
  "ADR 0016). Le re-fit qui corrigerait ce biais est BLOQUÉ faute de " +
  "données live neuves (MODE=paper depuis le 2026-08-14), pas parce que le " +
  "modèle serait validé."
);

function PaperCaveatBanner() {
  // Fail-CLOSED, pas fail-open : le bandeau est affiché PAR DÉFAUT au montage
  // avec le texte canonique en dur, avant même que la requête réseau parte.
  // L'appel à /api/paper-book-caveat ne sert qu'à (a) remplacer ce texte par
  // celui du serveur et (b) MASQUER le bandeau quand le serveur confirme
  // explicitement un mode != paper. Si l'appel échoue, le bandeau RESTE
  // affiché (texte de repli, préfixé "Mode non confirmé —") : montrer
  // l'avertissement à tort en live coûte une phrase inutile ; le masquer à
  // tort en paper laisse lire un livre optimiste sans le savoir — les deux
  // erreurs ne se valent pas, donc en cas de doute on avertit.
  const [texte, setTexte] = useState(AVERTISSEMENT_LIVRE_PAPIER_REPLI);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPaperBookCaveat()
      .then((data) => {
        if (cancelled) return;
        if (data && data.mode === 'paper') {
          setTexte(data.texte || AVERTISSEMENT_LIVRE_PAPIER_REPLI);
          setVisible(true);
        } else if (data) {
          // Mode confirmé explicitement != paper : seul cas où on masque.
          setVisible(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Échec réseau : le mode n'est PAS confirmé. On garde le bandeau
        // (fail-closed) avec le texte de repli, préfixé pour signaler
        // l'incertitude plutôt que de prétendre avoir vérifié le mode.
        setTexte(`Mode non confirmé — ${AVERTISSEMENT_LIVRE_PAPIER_REPLI}`);
        setVisible(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  return (
    <div className="paper-caveat-banner" role="alert">
      <AlertTriangle size={16} />
      <span>{texte}</span>
    </div>
  );
}

function ProtectedRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-secondary)' }}>
        Chargement...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Outlet />
      <PaperCaveatBanner />
    </>
  );
}

export default ProtectedRoute;
